import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Shimmer from 'react-shimmer-effect';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import {
  format, parseISO, isSameDay, addDays,
} from 'date-fns';

import Button from '../../../../shared/_TBComponents/Button';
import Text from '../../../../shared/_TBComponents/Text';
import PreviewCalendarList from './PreviewCalendarList';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  max-width: ${({ $maxWidth }) => $maxWidth || '700px'};
  border: 1px solid #EEE;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 16px 24px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
  width: 100%;
`;

const LoadingTitle = styled.div`
  height: 26px;
  border-radius: 24px;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 0;
`;

const PanelContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
`;

const ProgramDayNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 65%;

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`;

const CopyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  width: 35%;
  border-left: 2px solid #EEE;
  padding-left: 16px;
  gap: 12px;

  @media screen and (max-width: 767px) {
    width: 100%;
    border-left: none;
    padding-left: 0;
  }
`;

const NavigatorPreviewPanel = ({
  type,
  maxWidth,
  previewCopy,
}) => {
  const [programDayIndex, setProgramDayIndex] = useState(0);

  const calendarItems = useSelector(
    (state) => state.navigator.data.calendarItems || [],
  );
  const calendarWorkoutDate = useSelector(
    (state) => state.navigator.data.calendarWorkoutDate,
  );
  const calendarItemsError = useSelector(
    (state) => state.navigator.data.calendarItemsError,
  );
  const isCalendarItemsLoading = useSelector(
    (state) => state.navigator.ui.isLoadingCalendarItems,
  );

  const programPreviewItems = useSelector(
    (state) => state.navigator.data.programPreviewItems || [],
  );
  const programPreviewDays = useSelector(
    (state) => state.navigator.data.programPreviewDays || [],
  );
  const programPreviewInitialDayIndex = useSelector(
    (state) => state.navigator.data.programPreviewInitialDayIndex || 0,
  );
  const programPreviewError = useSelector(
    (state) => state.navigator.data.programPreviewError,
  );
  const isProgramPreviewLoading = useSelector(
    (state) => state.navigator.ui.isLoadingProgramPreview,
  );

  const isCalendar = type === 'calendar';

  const programDays = useMemo(() => {
    if (isCalendar) return [];
    if (!programPreviewDays.length) {
      return [{
        label: 'Week 1 - Day 1',
        items: programPreviewItems || [],
      }];
    }

    return programPreviewDays.map((day, idx) => ({
      label: `Week ${day.weekNumber || Math.floor((day.dayNumber || idx) / 7) + 1} - Day ${day.dayInWeek || ((day.dayNumber || idx) % 7) + 1}`,
      items: day.exercises || [],
    }));
  }, [isCalendar, programPreviewDays, programPreviewItems]);

  useEffect(() => {
    if (isCalendar) return;
    const maxIndex = Math.max(0, programDays.length - 1);
    setProgramDayIndex(Math.min(programPreviewInitialDayIndex, maxIndex));
  }, [isCalendar, programPreviewInitialDayIndex, programDays.length]);

  const activeProgramDay = programDays[programDayIndex] || { label: 'Day 1', items: [] };
  const workoutItems = isCalendar ? calendarItems : activeProgramDay.items;
  const isLoading = isCalendar ? isCalendarItemsLoading : isProgramPreviewLoading;
  const previewError = isCalendar ? calendarItemsError : programPreviewError;

  const formatWorkoutDate = (dateStr) => {
    if (!dateStr) return null;
    const date = parseISO(dateStr);
    const today = new Date();
    if (isSameDay(date, today)) return 'Today';
    if (isSameDay(date, addDays(today, 1))) return 'Tomorrow';
    return format(date, 'MMMM d');
  };

  const label = isCalendar
    ? calendarWorkoutDate && `Next Scheduled Workout: ${formatWorkoutDate(calendarWorkoutDate)}`
    : activeProgramDay.label;

  return (
    <Panel $maxWidth={maxWidth}>
      {(label || isLoading) && (
        <HeaderRow>
          {isLoading ? (
            <Shimmer>
              <LoadingTitle style={{ width: isCalendar ? '280px' : '72px' }} />
            </Shimmer>
          ) : label && (
            <Text
              styledTextProps={{ fontSize: '18px', fontWeight: 700 }}
              styledWrapperProps={{ padding: '6px 12px 6px 0' }}
            >
                {label}
            </Text>
          )}
          {!isCalendar && (
            <ProgramDayNav>
              <Button
                name='Previous program day'
                icon='left-arrow'
                iconOnly
                iconSize={12}
                iconColor='#444444'
                bgColor='#FFF'
                borderColor='#444444'
                customBorderRadius='999px'
                noHover
                disabled={programDayIndex === 0}
                onClick={() => setProgramDayIndex((prev) => Math.max(0, prev - 1))}
                styledWrapperProps={{
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  p: '0px',
                }}
              />
              <Button
                name='Next program day'
                icon='right-arrow'
                iconOnly
                iconSize={12}
                iconColor='#444444'
                bgColor='#FFF'
                borderColor='#444444'
                customBorderRadius='999px'
                noHover
                disabled={programDayIndex >= programDays.length - 1}
                onClick={() => setProgramDayIndex(
                  (prev) => Math.min(programDays.length - 1, prev + 1),
                )}
                styledWrapperProps={{
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  p: '0px',
                }}
              />
            </ProgramDayNav>
          )}
        </HeaderRow>
      )}
      {!isLoading && previewError ? (
        <ErrorState>
          <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
            Error loading preview
          </Text>
          <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
            {previewError}
          </Text>
        </ErrorState>
      ) : (
        <>
          {isCalendar && (
          <PreviewCalendarList
            workoutItems={workoutItems}
            isWorkoutsListLoading={isLoading}
          />
          )}
          {!isCalendar && (
          <PanelContainer>
            <ListWrapper>
              <PreviewCalendarList
                workoutItems={workoutItems}
                isWorkoutsListLoading={isLoading}
              />
            </ListWrapper>
            <CopyWrapper>
              {previewCopy}
            </CopyWrapper>
          </PanelContainer>
          )}
        </>
      )}
    </Panel>
  );
};

NavigatorPreviewPanel.propTypes = {
  type: PropTypes.oneOf(['calendar', 'program']).isRequired,
  maxWidth: PropTypes.string,
  previewCopy: PropTypes.node,
};

NavigatorPreviewPanel.defaultProps = {
  maxWidth: undefined,
  previewCopy: null,
};

export default NavigatorPreviewPanel;
